import {
  ACK_SOUND_CONFIG,
  DEFAULT_ACK_SOUND,
  type AckSoundId,
} from './ack-sound.consts';

function getAudioContextCtor(): typeof AudioContext | undefined {
  const win: Window & { webkitAudioContext?: typeof AudioContext } = window;
  return window.AudioContext ?? win.webkitAudioContext;
}

const RESUME_TIMEOUT_MS = 300;

function isRunning(ctx: AudioContext): boolean {
  return ctx.state === 'running';
}

function closeQuietly(ctx: AudioContext): void {
  ctx.close().catch(() => undefined);
}

/**
 * A context created outside a user gesture (an SSE frame, a timer) starts suspended
 * in Safari and in a Chrome tab without prior interaction. Its clock never moves, so
 * nothing plays and the oscillator's `ended` — which closes it — never fires. Resume
 * it, and close it instead of playing when it stays suspended; `resume()` can hang
 * until the next gesture, hence the timeout.
 */
async function openRunningContext(): Promise<AudioContext | null> {
  const Ctx = getAudioContextCtor();
  if (!Ctx) return null;
  let ctx: AudioContext;
  try {
    ctx = new Ctx();
  } catch {
    return null;
  }
  if (!isRunning(ctx)) {
    await Promise.race([
      ctx.resume().catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, RESUME_TIMEOUT_MS)),
    ]);
  }
  if (isRunning(ctx)) return ctx;
  closeQuietly(ctx);
  return null;
}

/** No-ops if AudioContext is unavailable or blocked (e.g. autoplay policy). */
export function playAckSound(soundId: AckSoundId = DEFAULT_ACK_SOUND): void {
  if (typeof window === 'undefined') return;

  const preset = ACK_SOUND_CONFIG[soundId];
  if (!preset) return;

  if (preset.type === 'asset') {
    try {
      const audio = new Audio(preset.url);
      audio.volume = 0.8;
      void audio.play();
    } catch {
      // ignore
    }
    return;
  }

  void openRunningContext().then((ctx) => {
    if (!ctx) return;
    try {
      if (preset.type === 'tick') playTick(ctx, preset);
      else playChime(ctx, preset);
    } catch {
      closeQuietly(ctx);
    }
  });
}

function playTick(
  ctx: AudioContext,
  preset: { freq: number; dur: number; gain: number }
): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(preset.freq, now);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(preset.gain, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, now + preset.dur);
  osc.start(now);
  osc.stop(now + preset.dur);
  osc.addEventListener('ended', () => closeQuietly(ctx));
}

function playChime(
  ctx: AudioContext,
  preset: {
    tone1Freq: number;
    tone2Freq: number;
    gainLevel: number;
    tone1Dur: number;
    gap: number;
    tone2Dur: number;
  }
): void {
  const now = ctx.currentTime;
  const { tone1Freq, tone2Freq, gainLevel, tone1Dur, gap, tone2Dur } = preset;

  function playTone(
    startTime: number,
    frequency: number,
    duration: number
  ): OscillatorNode {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, startTime);
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(gainLevel, startTime + 0.008);
    gain.gain.setValueAtTime(gainLevel, startTime + duration - 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.start(startTime);
    osc.stop(startTime + duration);
    return osc;
  }

  playTone(now, tone1Freq, tone1Dur);
  const lastOsc = playTone(now + tone1Dur + gap, tone2Freq, tone2Dur);
  lastOsc.addEventListener('ended', () => closeQuietly(ctx));
}
